r<template>
  <div>
    <step-intro iframe-url="">
      <p>To ensure a well-structured and visually appealing thesis, proper preparation of your Word or LaTeX document is essential. In this step, we will guide you on how to format your document correctly and export it properly. <br><br>
      Keep in mind that the videos below are <b>aimed at Microsoft Word users</b>. The same adjustments in structure need to be applied for theses made in LaTeX as well, so please apply identical changes should you work in LaTeX.</p>
    </step-intro>

    <box title="Prepare Your Document">
      <h3 class="title is-3">General Preparations</h3>
      <p>
        Before finalizing your thesis, review the structure of your Word or LaTeX document. Most
        adjustments are minor, but checking is crucial. We cannot guarantee a quality thesis
        if the file is not prepared correctly. Please watch our instructional video. The content of the video is also summarized below.
      </p>
      <div class="iframe-container">
        <iframe
          src="https://player.vimeo.com/video/1058963917"
          frameborder="0"
          allow="autoplay; fullscreen"
          allowfullscreen
        ></iframe>
      </div>
      <br>
      <h4 class="title-is-4">Adding a Temporary Cover Page</h4>
      <p>
        All printed books start with a right-hand page. Keep this in mind when adjusting your document. Specifically Word is not designed for book
        formatting, so there, a temporary cover page is needed to simulate book layout. This ensures
        proper alignment of left and right pages in the final thesis.
      </p>

      <h4 class="title-is-4">First Pages Structure</h4>
      <p>Most theses follow this order for the first pages:</p>
      <ul>
        <li>Page 1: Title Page</li>
        <li>Page 2: Colophon</li>
        <li>Page 3: University Promotion Page (Example with screenshot)</li>
        <li>Page 4: Committee Page</li>
        <li>Page 5: Table of Contents</li>
      </ul>

      <h4 class="title-is-4">Page Numbers in Table of Contents</h4>
      <p>
        Ensure the page numbers in your table of contents are accurate. We will use the page
        numbers exactly as you provide them.
      </p>

      <h4 class="title-is-4">Chapter and Section Pages</h4>
      <p>
        Each chapter and section must start with a blank page on the left and a title page on
        the right (odd-numbered). Appendices should also follow this structure.
      </p>

      <h4 class="title-is-4">Page Size</h4>
      <p>
        All pages should be A4 (210mm x 297mm) or 170mm x 240mm. If larger page sizes (e.g., A3)
        are included, they may become unreadable when scaled down.
      </p>

      <h4 class="title-is-4">Inner Margins</h4>
      <p>
        It is best to use standard margin settings to ensure optimal text
        placement. Important content should not be placed in the inner margins.
      </p>

      <h4 class="title-is-4">Scaling and Font Size</h4>
      <p>
        Since A4 pages are scaled down for printing, we recommend using a font size of 11 or 12.
      </p>

      <h4 class="title-is-4">Landscape Pages</h4>
      <p>
        Rotated pages are allowed. However, if they contain headings or page numbers, these must
        be manually repositioned, as they will be rotated automatically. If you have chosen to have the page numbers placed by us, you may simply remove them alltogether.
      </p>

      <h4 class="title-is-4">No Blank Pages at the End</h4>
      <p>
        Do not manually add blank pages at the end. We will ensure the final page of your thesis
        is blank and insert extra blank pages as needed.
      </p>

      <h4 class="title-is-4">Removing the Temporary Cover Page</h4>
      <p>
        Before exporting your thesis, delete the temporary cover page.
      </p>
      <br>
      <p>
      <i>If you're having trouble setting up the preparations above, or if your dissertation includes elements that need to differ from our recommended settings, get in touch with our <a href="https://www.persoonlijkproefschrift.nl/contact/" target="_blank">coordinator</a>, and we'll help you find a solution.</i>
      </p>

      <template v-if="headerAndPageNumbers">
        <h3 class="title is-3">Headings and Page Numbers</h3>
        <p>
          Since you opted for template-based headings and page numbers, remove any existing ones
          from your document. We will add them during the formatting process. You will
          specify the heading text in Step 5.
        </p>
        <div class="iframe-container">
          <iframe
            src="https://player.vimeo.com/video/1058964091"
            frameborder="0"
            allow="autoplay; fullscreen"
            allowfullscreen
          ></iframe>
        </div>
      </template>

      <template v-if="chapterPages">
        <h3 class="title is-3">Chapter Pages</h3>
        <p>
          Since you chose to add custom chapter pages, ensure that your document has the
          necessary two pages to place them in to. In Step 5, you will provide chapter titles,
          authors, and publication details.
        </p>
        <p>
          If you have enlisted one of our designers for your chapter pages, they will insert the
          chapter pages for you. If you want to include your own image on the left page of each
          chapter section, upload it via WeTransfer along with your thesis.
        </p>
        <p>
          <strong>IMPORTANT:</strong> Ensure that no other content exists on your current
          chapter pages, as it will be replaced.
        </p>
        <div class="iframe-container">
          <iframe
            src="https://player.vimeo.com/video/1058963906"
            frameborder="0"
            allow="autoplay; fullscreen"
            allowfullscreen
          ></iframe>
        </div>
      </template>
      <br>
      <h4 class="title-is-3">Exporting Your Thesis as a PDF-file</h4>
      <p>
        A PDF-file looks identical to every computer or printer, so this is the preferred file type to export your thesis in. To export your Word-file as a PDF, in Microsoft Word, select "Save as...". Then, next to "Save as type:", select PDF. You can find detailed instructions by Microsoft over
        <a
          href="https://support.microsoft.com/en-us/office/export-word-document-as-pdf-4e89b30d-9d7d-4866-af77-3af5536b974c"
          target="_blank"
          >here</a>. Be sure to hide any markups (i.e. comments) in Word that might be present in your file before exporting, as these will show up in the final PDF otherwise.<br><br>
          For theses made in LaTeX, the method of exporting to PDF depends on your approach to LaTeX. Please refer to your relevant documentation, or alternatively consult Google or our coordinator.
      </p>
    </box>

    <box title="Final PDF-document">
      <p>
        The PDF-file must be correctly formatted to ensure a high-quality final version.
      </p>
      <div class="field">
        <b-checkbox v-model="watchedVideo">I have watched the instruction video(s).</b-checkbox>
      </div>
      <div class="field">
        <b-checkbox v-model="chaptersUneven"
          >I have made all necessary adjustments to my document.</b-checkbox
        >
      </div>
      <div class="field">
        <b-checkbox v-model="layoutCorrect"
          >I have exported my document as a PDF-file.</b-checkbox
        >
      </div>
      <hr />
      <div class="columns is-vcentered">
        <div class="column is-narrow has-text-grey-dark">Upload your final PDF-file</div>
        <div class="column">
          <b-field>
            <b-button
              type="is-primary"
              @click.prevent="showUploadField = true"
              :disabled="!userCanUploadFile"
              >Upload File</b-button
            >
          </b-field>
        </div>
      </div>
      <div v-show="showUploadField">
        <validation-observer tag="div" :step-index="stepIndex">
          <p>
            Upload a new file to
            <b-tooltip
              multilined
              label="Select 'Transfer link' instead of 'E-mail transfer'."
              ><a href="https://www.wetransfer.com" target="_blank">WeTransfer</a
            ></b-tooltip>
            and paste the url in the field below.
          </p>
          <validation-provider rules="required|url" v-slot="{ errors }">
            <b-field :type="{ 'is-danger': errors[0] }" :message="errors">
              <b-input
                icon="link"
                placeholder="https://www.wetransfer.com"
                v-model="thesisFile"
                type="url"
              ></b-input>
            </b-field>
          </validation-provider>
        </validation-observer>
      </div>
    </box>
  </div>
</template>

<script>
import { mapFields } from 'vuex-map-fields';
import box from './../components/box.vue';
import stepIntro from './../components/step-intro.vue';
import ValidationObserver from './../components/validation-observer.vue';

export default {
  props: ['stepIndex'],
  components: {
    box,
    stepIntro,
    ValidationObserver
  },
  computed: {
    ...mapFields([
      'thesisFile',
      'uploadRequirements.watchedVideo',
      'uploadRequirements.chaptersUneven',
      'uploadRequirements.layoutCorrect',
      'templateOptions.elements.headerAndPageNumbers',
      'templateOptions.elements.tabs',
      'templateOptions.elements.chapterPages'
    ]),
    userCanUploadFile() {
      return (
        this.$store.state.uploadRequirements.watchedVideo &&
        this.$store.state.uploadRequirements.chaptersUneven &&
        this.$store.state.uploadRequirements.layoutCorrect
      );
    }
  },
  data() {
    return {
      showUploadField: false
    };
  }
};
</script>

<style>
</style>
